import { ArrowUpRight } from "lucide-react";

import type { RetrievalDocument } from "@/lib/retrieval";
import { cn } from "@/lib/utils";

/**
 * One retrieved legal document, set as an annotated statute ("Counsel",
 * 2026-08-29): the citation and its verification chain live in a margin
 * column like annotations, and the verbatim law is the largest, darkest
 * text in the card — the law is the payload, never the dimmest line.
 * Shared between the live stream view and the printable report, so the two
 * can never disagree about what a document looks like.
 *
 * The verification chain (from the Instrument direction) renders the
 * provenance the backend already proves — retrieved / identity / effective /
 * source kind — as visible marginalia instead of buried metadata.
 *
 * Copy discipline: currency claims describe the mechanism ("retrieved
 * {date}", "amended since verification") and never the superlative ("always
 * current") — that phrasing is a recorded legal red line.
 */
export function DocumentCard({
  document,
  compact = false,
}: {
  document: RetrievalDocument;
  compact?: boolean;
}) {
  const explanation = document.extra?.explanation;
  const provenance = document.extra?.provenance;

  if (document.status === "not_applicable") {
    // Curated guidance for a topic this layer honestly has no general rule
    // for (e.g. zoning at the federal layer). Not law and not a failure.
    return (
      <StatusRow label="Regulated locally">
        <p className="font-serif italic text-body-sm leading-relaxed text-muted">
          {document.text}
        </p>
      </StatusRow>
    );
  }

  if (document.status === "no_results") {
    return (
      <StatusRow label="No matching rules">
        <p className="font-serif italic text-body-sm leading-relaxed text-muted">
          Nothing in this layer&apos;s rules matched this topic. Try more
          specific wording
          {document.url ? (
            <>
              , or browse the official source at <SourceLink url={document.url} />
            </>
          ) : null}
          .
        </p>
      </StatusRow>
    );
  }

  if (document.status === "not_yet_supported") {
    return (
      <StatusRow label="Not yet covered">
        <p className="font-serif italic text-body-sm leading-relaxed text-muted">
          Prosa cannot retrieve this layer&apos;s text yet.
          {document.url ? (
            <>
              {" "}
              The official source is <SourceLink url={document.url} />.
            </>
          ) : null}
        </p>
      </StatusRow>
    );
  }

  if (document.status === "unavailable") {
    return (
      <StatusRow label="Temporarily unavailable" tone="warn">
        <p className="font-serif italic text-body-sm leading-relaxed text-muted">
          {document.citation ? (
            <span className="cite not-italic text-text">{document.citation} — </span>
          ) : null}
          This source did not answer, or its page no longer matches the
          citation, so nothing is shown rather than the wrong text.
          {document.url ? (
            <>
              {" "}
              Read it directly at <SourceLink url={document.url} />.
            </>
          ) : null}
        </p>
      </StatusRow>
    );
  }

  const passage = explanation?.key_passage || excerpt(document.text, compact);
  const chain = verificationChain(document);

  return (
    <div className="grid gap-x-6 gap-y-2 sm:grid-cols-[168px,1fr]">
      {/* Marginalia: the citation and its verification chain. */}
      <div className="sm:text-right">
        {document.citation ? (
          <p className="cite font-medium leading-snug text-primary">
            {document.citation}
          </p>
        ) : null}
        <ul className="mt-1.5 space-y-0.5 font-mono text-[11px] leading-relaxed text-muted">
          {chain.map((item) => (
            <li key={item.text}>
              <span
                className={cn(
                  item.tone === "ok" && "text-success",
                  item.tone === "warn" && "text-warn",
                )}
              >
                {item.mark}
              </span>{" "}
              {item.text}
            </li>
          ))}
        </ul>
      </div>

      {/* The document itself: heading, then the law at full ink. */}
      <div className="min-w-0">
        {document.heading ? (
          <h4 className="font-serif text-[17px] font-semibold leading-snug text-ink">
            {document.heading}
          </h4>
        ) : null}

        {passage ? (
          <blockquote
            className={cn(
              "mt-2 font-serif text-[15px] leading-relaxed text-ink",
              compact && "line-clamp-6",
            )}
          >
            <span aria-hidden="true" className="font-semibold text-primary">
              §{" "}
            </span>
            {passage}
          </blockquote>
        ) : null}

        {explanation?.explanation ? (
          <p className="mt-2.5 border-t border-border pt-2 text-body-sm text-muted">
            {explanation.explanation}
          </p>
        ) : null}

        {document.url ? (
          <div className="mt-2.5 text-body-sm">
            <SourceLink url={document.url} label="Read the official source" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The marginalia verification chain: only claims the backend proved. */
function verificationChain(document: RetrievalDocument) {
  const provenance = document.extra?.provenance;
  const items: { mark: string; text: string; tone: "ok" | "warn" | "muted" }[] = [];

  if (document.retrieved_at) {
    items.push({
      mark: "✓",
      text: `retrieved ${formatDate(document.retrieved_at)}`,
      tone: "ok",
    });
  }
  if (provenance?.identity_markers?.length) {
    items.push({ mark: "✓", text: "identity verified", tone: "ok" });
  }
  if (provenance?.effective_date) {
    if (provenance.stale) {
      items.push({
        mark: "△",
        text: "amended since verification",
        tone: "warn",
      });
    } else {
      items.push({
        mark: "✓",
        text: `effective ${provenance.effective_date}`,
        tone: "ok",
      });
    }
  }
  if (provenance?.source_kind === "agency_guidance") {
    items.push({ mark: "◇", text: "agency guidance, not code", tone: "muted" });
  }
  if (provenance && provenance.curated === false) {
    items.push({ mark: "◇", text: "found by live search", tone: "muted" });
  }
  return items;
}

/** A non-ok status, set as a quiet annotated aside — small-caps label in the
 * margin, a calm serif sentence in the text column. No alarm badges: a
 * coverage gap is a fact, not a failure. */
function StatusRow({
  label,
  tone = "muted",
  children,
}: {
  label: string;
  tone?: "muted" | "warn";
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-x-6 gap-y-1 sm:grid-cols-[168px,1fr]">
      <p
        className={cn(
          "font-mono text-[11px] uppercase tracking-[0.08em] sm:pt-0.5 sm:text-right",
          tone === "warn" ? "text-warn" : "text-muted",
        )}
      >
        {label}
      </p>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SourceLink({ url, label }: { url: string; label?: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 cite not-italic text-primary hover:text-primary-hover hover:underline"
    >
      {label ?? hostname(url)}
      <ArrowUpRight className="h-3 w-3" />
    </a>
  );
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Source";
  }
}

function excerpt(text: string | null, compact: boolean): string | null {
  if (!text) return null;
  const limit = compact ? 420 : 900;
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > limit / 2 ? lastSpace : limit)}…`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
